/**
 * Deterministic avatar gradient colours derived from a seed (the user's email).
 *
 * Pure module — no StyleX / DOM dependencies — so it can be unit-tested without
 * the StyleX babel transform (mirrors `theme/_contrast-util.ts`).
 *
 * The same seed always yields the same gradient, and every colour stop is tuned
 * to clear WCAG AA contrast against the white avatar initials.
 */

/** FNV-1a — small, stable, platform-independent string hash. */
function hashString(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

/** Gamma-decode an 8-bit sRGB channel to linear light (0..1). */
function srgbToLinear(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of an sRGB colour (0..1). */
function relLuminance(r: number, g: number, b: number): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/** Contrast ratio of a colour (given its luminance) against white text. */
function contrastWithWhite(lum: number): number {
  return 1.05 / (lum + 0.05);
}

/**
 * Pick the brightest lightness for a hue whose colour still clears WCAG AA
 * (>= 4.6:1) against white text, so the white initials are always legible.
 * Equalising on contrast also gives every hue a consistent perceived depth.
 */
function contrastSafeColor(hue: number, sat: number, darken = 1): string {
  let l = 0.5;
  for (let i = 0; i < 50 && l > 0.12; i++) {
    const [r, g, b] = hslToRgb(hue, sat, l);
    if (contrastWithWhite(relLuminance(r, g, b)) >= 4.6) break;
    l -= 0.01;
  }
  l = Math.max(0.12, l * darken);
  const [r, g, b] = hslToRgb(hue, sat, l);
  return `rgb(${r} ${g} ${b})`;
}

/**
 * Deterministic two-stop gradient derived from a seed (the user's email).
 * The same seed always yields the same colours; both stops are guaranteed
 * to satisfy WCAG AA against the white initials.
 */
export function avatarGradient(seed?: string | null): { c1: string; c2: string } {
  const key = (seed ?? '').trim().toLowerCase();
  const hue = hashString(key) % 360;
  return {
    c1: contrastSafeColor(hue, 0.62),
    c2: contrastSafeColor(hue + 26, 0.62, 0.86),
  };
}
